import hintsSocketHandler from '@/utils/hintsSocketHandler'
import EventBus from '@/eventBus'
import * as Sentry from '@sentry/vue'
import QuizStatuses from '@/constants/QuizStatuses'
import store from '@/store'

const MESSAGE_TYPES = {
  SLIDE: 'slide',
  CLEAR_SLIDE_DATA: 'slideClearData',
  UPDATE_CANVAS_BLOCKS: 'update-canvas-blocks',
  SORT_SLIDE: 'sort-slide',
  ACCEPT_TYPE_ANSWER: 'acceptTypeAnswer',
  SPIN_THE_WHEEL: 'spinner-wheel',
  QUIZ_HINTS: 'hints',
}

function checkMultipleChoiceField(doc, slide) {
  if (
    !doc.data.multipleChoice &&
    doc.data.multipleChoice != slide.multipleChoice
  ) {
    for (let i = 0; i < slide.SlideOptions.length; i++) {
      if (slide.SlideOptions[i].selected) {
        slide.radioSelectded = slide.SlideOptions[i].id
        break
      }
    }
  }
}

function checkLimitChoiceField(doc, slide) {
  if (doc.data.limitChoice != slide.limitChoice) {
    let count = 0
    for (let i = 0; i < slide.SlideOptions.length; i++) {
      if (slide.SlideOptions[i].selected) {
        count++
      }

      if (count > doc.data.limitChoice) {
        slide.SlideOptions[i].selected = false
        slide.SlideOptions[i].disabled = true
      } else {
        slide.SlideOptions[i].disabled = false
      }
    }
  }
}

async function decryptSlide(doc, slide, rootComponent) {
  if (doc.isEncrypted) {
    doc.data = await rootComponent.decryptSlide(doc, slide.id)
  }
}

function updateSlideColors(doc, slide) {
  if (slide.textColour != doc.data.textColour) {
    document.body.style.color = doc.data.textColour
    slide.textColour = doc.data.textColour
  }

  if (slide.baseColour != doc.data.baseColour) {
    slide.baseColour = doc.data.baseColour
    document.body.style.backgroundColor = doc.data.baseColour
  }
  if (document.getElementById('qna-session-box')) {
    document.getElementById('qna-session-box').style.backgroundColor =
      slide.baseColour
    document.getElementById('qna-session-box').style.color =
      slide.textColour
  }
}

function updateSlideImages(doc, slide) {
  if (slide.image !== doc.data.image) {
    if (!doc.data.image) {
      slide.image = null
    } else if (!slide.image || (slide.image.name !== doc.data.image.name)) {
      slide.image = doc.data.image
    }
  }

  if (slide.backgroundImage !== doc.data.backgroundImage) {
    if (!doc.data.backgroundImage) {
      slide.backgroundImage = null
    } else if (
      !slide.backgroundImage
      || slide.backgroundImage.name !== doc.data.backgroundImage.name
    ) {
      slide.backgroundImage = doc.data.backgroundImage
    }
  }
}

function updateOtherSlideData(doc, slide, rootComponent) {
  checkMultipleChoiceField(doc, slide)
  checkLimitChoiceField(doc, slide)

  slide.title = doc.data.title
  slide.sanitizedTitle = doc.data.sanitizedTitle
  slide.titleHTML = doc.data.titleHTML
  slide.sanitizedTitleHTML = doc.data.sanitizedTitleHTML
  slide.bodyHTML = doc.data.bodyHTML
  slide.titleDesc = doc.data.titleDesc
  slide.deleted = doc.data.deleted
  slide.openEndedMultipleOption = doc.data.openEndedMultipleOption
  slide.multipleChoice = doc.data.multipleChoice
  slide.stopSubmission = doc.data.stopSubmission
  slide.subheading = doc.data.subheading
  slide.bulletPointsIndex = doc.data.bulletPointsIndex
  slide.showAllBulletPoints = doc.data.showAllBulletPoints
  slide.imageCaption = doc.data.imageCaption
  slide.imageType = doc.data.imageType
  slide.slideType = doc.data.slideType
  slide.textAlign = doc.data.textAlign
  slide.otherCorrectQuiz = doc.data.otherCorrectQuiz
  slide.correctQuizTypeAnswer = doc.data.correctQuizTypeAnswer
  slide.audioName = doc.data.audioName
  slide.audioShowControl = doc.data.audioShowControl
  slide.audioAutoPlay = doc.data.audioAutoPlay
  slide.audioRepeat = doc.data.audioRepeat
  slide.audioPlayOnAudience = doc.data.audioPlayOnAudience
  slide.audioAudienceShowControl = doc.data.audioAudienceShowControl
  slide.minPoint = doc.data.minPoint
  slide.maxPoint = doc.data.maxPoint
  slide.showIframe = doc.data.showIframe
  slide.showYouTubeIframe = doc.data.showYouTubeIframe
  slide.isCorrectGetPoint = doc.data.isCorrectGetPoint
  slide.metadata = doc.data.metadata
  slide.showVotingResultsOnAudience = doc.data.showVotingResultsOnAudience
  slide.showPercentage = doc.data.showPercentage
  slide.imageSubmission = doc.data.imageSubmission
  slide.publishedLink = doc.data.publishedLink
  slide.youtubeLink = doc.data.youtubeLink
  slide.visibility = doc.data.visibility
  slide.timeToAnswer = doc.data.timeToAnswer
  slide.fastAnswerGetMorePoint = doc.data.fastAnswerGetMorePoint
  slide.questionIndex = doc.data.questionIndex
  slide.questionCount = doc.data.questionCount
  slide.voteStep = doc.data.voteStep
  slide.hideResult = doc.data.hideResult
  slide.showCorrectOption = doc.data.showCorrectOption
  slide.scale = doc.data.scale
  slide.hasTimeLimit = doc.data.hasTimeLimit
  slide.randomColours = doc.data.randomColours
  slide.addCorrectOption = doc.data.addCorrectOption

  if (slide.stopSubmissionTime !== doc.data.stopSubmissionTime) {
    slide.stopSubmissionTime = doc.data.stopSubmissionTime
  }

  if (slide.entriesPerParticipant != doc.data.entriesPerParticipant) {
    slide.entriesPerParticipant = doc.data.entriesPerParticipant
  }

  if (!doc.data.additionalFields) {
    slide.additionalFields = []
  }

  if (!doc.data.otherCorrectQuiz) {
    slide.otherCorrectQuiz = []
  }

  try {
    slide.additionalFields = JSON.parse(doc.data.additionalFields)
  } catch (error) {
    Sentry.captureException(error)
    slide.additionalFields = []
  }

  try {
    slide.otherCorrectQuiz = JSON.parse(doc.data.otherCorrectQuiz)
  } catch (error) {
    Sentry.captureException(error)
    slide.otherCorrectQuiz = []
  }

  rootComponent.$store.dispatch('updateSlide', slide)
}

function updateSlideType(doc, slide, rootComponent) {
  if (slide.type != doc.data.type) {
    rootComponent.dataSlideNext = {
      presentation: {
        accessCode: rootComponent.$store.state.presentation.uniqueAccessCode,
      },
      id: slide.id,
    }
    rootComponent.confirmNextSlide()
  }
}

const handleSubmitQuizTimeout = async (doc) => {
  if (store.getters['featureToggles/canUseFixMqxMissMessageSubmitQuiz']) {
    const slideSubmited = store.getters['submitedQuiz/slideSubmited']
    if (slideSubmited[doc.data.id] && slideSubmited[doc.data.id] === 'waiting') {
      await store.dispatch('submitedQuiz/getResulFromAhaSyncAction', doc.data.id)
    }
  }
  EventBus.$emit('quizTimeout', doc.data.id)
  EventBus.$emit('stop-progress-bar-quiz', doc.data.id)
}

function updateQuizStatus(doc, slide, rootComponent) {
  if (!rootComponent.presentation.audiencePacing) {
    if (doc.data.quizTimestamp) {
      slide.quizTimestamp = doc.data.quizTimestamp
    }

    if (doc.data.quizStatus) {
      slide.quizStatus = doc.data.quizStatus
    }
  }

  if (slide.quizResultCache !== doc.data.quizResultCache) {
    slide.quizResultCache = doc.data.quizResultCache
  }

  if (slide.type === 'pickAnswer' && !rootComponent.$store.state.presentation.audiencePacing) {
    slide.limitChoice = rootComponent.$store.getters.activeActivity.SlideOptions.length

    if (doc.data.quizStatus == QuizStatuses.COUNTDOWN_5S_SCREEN) {
      EventBus.$emit('startQuizScreenTwo', doc.data.id)
    }

    if (doc.data.quizStatus == QuizStatuses.RESULT_SCREEN) {
      handleSubmitQuizTimeout(doc)
    }

    if (
      !rootComponent.$store.state.presentation.presenting &&
      doc.data.quizStatus > 1
    ) {
      rootComponent.$store.state.presentation.presenting = true
    }
  } else if (doc.data.limitChoice !== undefined) {
    // only set limitChoice is the data is available.
    // In case of mqtt, sample data looks like this
    // {"_from":"live","type":"slide","action":"update","data":{"id":620479,"isNextSlide":true,"timestamp":1767329470171}}
    // and can cause the limitChoice value to be overwritten to undefined
    slide.limitChoice = doc.data.limitChoice
  }
}

function updateQuizHints(doc) {
  if (doc.action === 'hints') {
    hintsSocketHandler.handleHintsDataFromPresenter(doc.data)
  }
}

async function handleSlideMessages(doc, rootComponent) {
  const slide = rootComponent.$store.getters.activeActivity
  await decryptSlide(doc, slide, rootComponent)
  if (slide.id !== doc.data.id) return

  if (doc._from !== 'live') {
    updateSlideColors(doc, slide)
    updateSlideImages(doc, slide)
  }

  if (store.getters['featureToggles/canUseNewChangeSlideMechanism'] && doc._from === 'live') {
    updateQuizStatus(doc, slide, rootComponent)
    rootComponent.$store.dispatch('updateSlide', doc.data)
    return
  }
  if (!store.getters['featureToggles/canUseNewChangeSlideMechanism']) {
    if (slide.quizStatus == 4 && doc.data.quizStatus == 1 && slide.id == doc.data.id && doc.trigger && doc.trigger.indexOf('updateSlideToQuizLobbyScreen') > -1) {
      Sentry.captureException(new Error('slide.websocket.subscriber: updateQuizStatus from question screen to lobby screen'), {
        extra: {
          trigger: doc.trigger,
          currentSlide: slide,
          updatedSlide: doc.data,
        },
      })
    }
    const shouldNotUpdateQuizStatusFromQuestionScreenToLobbyScreen = store.getters['featureToggles/disableUpdateQuizStatusFromQuestionScreenToLobbyScreen'] &&
      slide.quizStatus == QuizStatuses.QUESTION_SCREEN &&
      doc.data.quizStatus == QuizStatuses.LOBBY_SCREEN &&
      store.state?.presentation?.presenting &&
      slide.id == doc.data.id && doc.trigger?.includes('_handleOnReconnect')
    if (!shouldNotUpdateQuizStatusFromQuestionScreenToLobbyScreen) {
      updateQuizStatus(doc, slide, rootComponent)
    }
  }

  updateSlideType(doc, slide, rootComponent)
  updateOtherSlideData(doc, slide, rootComponent)
  updateQuizHints(doc)
}

function handleClearSlideData() {
  setTimeout(() => {
    window.location.reload()
  }, 2000)
}

function handleUpdateCanvasBlocks(doc) {
  EventBus.$emit('update-canvas-blocks', doc.data)
}

function handleSortSlide(doc, editor) {
  editor.isSortSlide = true
  setTimeout(() => {
    editor.isSortSlide = false
  }, 500)
}

function handleAcceptTypeAnswer(doc) {
  EventBus.$emit('acceptTypeAnswer', doc.data)
}

function handleSpinTheWheel(doc, rootComponent) {
  const slide = rootComponent.$store.getters.activeActivity
  if (doc.data) {
    if (!slide.metadata) rootComponent.$store.getters.activeActivity.metadata = {}
    rootComponent.$store.getters.activeActivity.metadata.wheelStatus = doc.data.wheelStatus
    if (doc.data.winner) {
      rootComponent.$store.getters.activeActivity.metadata.winner = doc.data.winner
    }
    rootComponent.$store.dispatch('updateSlide', rootComponent.$store.getters.activeActivity)
  }
}

function handleQuizHintMessage(doc) {
  let needSorting = false
  const actionNeedSorting = ['create', 'patch']
  if (actionNeedSorting.includes(doc.action)) {
    needSorting = true
  }
  hintsSocketHandler.handleCRUDHintsFromPresenter(doc.action, doc.data, needSorting)
}

const SlideWebSocketSubscriber = {
  [MESSAGE_TYPES.SLIDE]: handleSlideMessages,
  [MESSAGE_TYPES.CLEAR_SLIDE_DATA]: handleClearSlideData,
  [MESSAGE_TYPES.UPDATE_CANVAS_BLOCKS]: handleUpdateCanvasBlocks,
  [MESSAGE_TYPES.SORT_SLIDE]: handleSortSlide,
  [MESSAGE_TYPES.ACCEPT_TYPE_ANSWER]: handleAcceptTypeAnswer,
  [MESSAGE_TYPES.SPIN_THE_WHEEL]: handleSpinTheWheel,
  [MESSAGE_TYPES.QUIZ_HINTS]: handleQuizHintMessage,
}

export default SlideWebSocketSubscriber
